/**
 * EDOT Browser SDK initialisation.
 *
 * This module must be imported **before** any other application code so the
 * SDK can patch fetch/XHR and capture the document-load span from the very
 * beginning of the page lifecycle.
 *
 * Config is injected at build time from VITE_ environment variables (see
 * .env.local for local dev; set the same variables in Netlify / GitHub Pages
 * deploy settings for production).
 *
 * The write key is intentionally embedded in the bundle. The key only has
 * `event:write` scope so the worst an abuser can do is send fake telemetry —
 * no data can be read or deleted.
 */

import { startBrowserSdk } from "@elastic/opentelemetry-browser";
import type { Span } from "@opentelemetry/api";
import festivalInfo from "../../programme/festivalInfo.json";

// Read toggle configurations from either runtime JSON config or build-time env vars
const festivalConfigs = (festivalInfo as any).configs || {};

export const apmEnabled =
  import.meta.env.VITE_APM_ENABLED === "true" && festivalConfigs.apmEnabled !== false;

const trackInteractions =
  import.meta.env.VITE_APM_TRACK_INTERACTIONS !== "false" &&
  festivalConfigs.trackInteractions !== false;

const endpoint = import.meta.env.VITE_APM_OTLP_ENDPOINT as string | undefined;
const serviceName =
  (import.meta.env.VITE_APM_SERVICE_NAME as string | undefined) ?? "tilos-maraton";

// Fetch the sample rate from the environment, defaulting to 10% (0.1) for minimal traces
const sampleRate = Number.parseFloat(
  festivalConfigs.trackInteractions ?? import.meta.env.VITE_APM_SAMPLING_RATIO ?? "0.1",
);

const debug = window.location.href.includes("localhost") ? console.debug : () => {};

// Anonymous session ID generator to trace user sessions without tracking PII
const getSessionId = (): string => {
  try {
    let sid = sessionStorage.getItem("apm_session_id");
    if (!sid) {
      sid = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("apm_session_id", sid);
    }
    return sid;
  } catch (e) {
    return "anonymous";
  }
};

const getConsentId = (): string | undefined => {
  try {
    return localStorage.getItem("user_consent_id") || undefined;
  } catch (e) {
    return undefined;
  }
};

let isInitialized = false;

export const initApm = (): void => {
  if (isInitialized) return;
  if (!apmEnabled || !endpoint) {
    console.warn("[APM] SDK not starting: enabled or endpoint missing", {
      enabled: apmEnabled,
      endpoint: !!endpoint,
      serviceName: !!serviceName,
    });
    return;
  }

  // Check cookie consent status
  const consentStatus = localStorage.getItem("cookie_consent_status");
  if (consentStatus !== "accepted") {
    console.info("[APM] OpenTelemetry initialization deferred: cookie consent not accepted.");
    return;
  }

  isInitialized = true;
  console.info("[APM] Initializing OpenTelemetry SDK");

  startBrowserSdk({
    serviceName,
    serviceVersion: __APP_VERSION__,
    otlpEndpoint: `${window.location.origin}${endpoint}/`.replace(/(\/)+$/, "/"),
    sampleRate,
    // Reduce console noise in production; use 'warn' locally to diagnose.
    logLevel: import.meta.env.DEV ? "warn" : "error",
    // Resource attributes are attached to all telemetry signals
    resourceAttributes: {
      "deployment.environment.name": import.meta.env.MODE,
      "session.id": getSessionId(),
      "user.id": getConsentId() || getSessionId(),
    },
    instrumentations: {
      "@opentelemetry/instrumentation-document-load": { enabled: false }, // Keep for basic initial load stats
      "@opentelemetry/instrumentation-fetch": {
        ignoreUrls: [
          new RegExp(endpoint),
          /\.(png|jpg|jpeg|gif|svg|webp|css|woff2?)$/i,
          /localhost:\d+\/vite/i,
        ],
      },
      "@opentelemetry/instrumentation-long-task": { enabled: false }, // Heavy tracing generator, disabled
      "@opentelemetry/instrumentation-xml-http-request": { enabled: false }, // App uses fetch API, disabled XHR
      "@opentelemetry/instrumentation-web-exception": {}, // Vital for runtime error reporting
      "@opentelemetry/instrumentation-web-vitals": {}, // Vital for minimal performance monitoring
      "@opentelemetry/instrumentation-user-interaction": trackInteractions
        ? {
            shouldPreventSpanCreation: (_eventType: string, element: HTMLElement, span: Span) => {
              // Find the closest interactive ancestor element (e.g., button, link, tab)
              let current: HTMLElement | null = element;
              let interactiveElement: HTMLElement | null = null;

              while (current) {
                const currentTagName = current.tagName ? current.tagName.toLowerCase() : "";
                const currentRole =
                  typeof current.getAttribute === "function" ? current.getAttribute("role") : null;

                const isInteractive =
                  currentTagName === "button" ||
                  currentTagName === "a" ||
                  currentTagName === "input" ||
                  currentTagName === "select" ||
                  currentTagName === "textarea" ||
                  currentRole === "button" ||
                  currentRole === "link" ||
                  currentRole === "tab";

                if (isInteractive) {
                  interactiveElement = current;
                  break;
                }
                if (currentTagName === "body" || currentTagName === "html") {
                  break;
                }
                current = current.parentElement;
              }

              if (!interactiveElement) {
                return true; // Exclude non-interactive clicks to reduce span noise
              }

              const tagName = interactiveElement.tagName.toLowerCase();

              // Attach click/element contextual data from the interactive element
              span.setAttribute("click.target.tag", tagName);
              if (interactiveElement.id) {
                span.setAttribute("click.target.id", interactiveElement.id);
              }

              const targetRole =
                interactiveElement.getAttribute("role") ||
                interactiveElement.getAttribute("aria-role");
              if (targetRole) {
                span.setAttribute("click.target.role", targetRole);
              }
              const targetLabel =
                interactiveElement.getAttribute("label") ||
                interactiveElement.getAttribute("aria-label");
              if (targetLabel) {
                span.setAttribute("click.target.label", targetLabel);
              }

              // Get text content safely and limit size
              let text = (interactiveElement.textContent || interactiveElement.innerText || "")
                .trim()
                .slice(0, 80);
              if (text) {
                span.setAttribute("click.target.text", text);
              }

              if (tagName === "a") {
                const href = interactiveElement.getAttribute("href");
                if (href) {
                  span.setAttribute("click.target.href", href);
                }
              }

              debug("sending span", {
                text,
                targetLabel,
                targetRole,
              });

              // Rename the span to represent the element handling the click
              span.updateName(`click on ${tagName}`);

              // Contextual path/URL
              span.setAttribute("click.page.url", window.location.href);
              span.setAttribute("click.page.path", window.location.pathname);

              return false; // Record the user interaction span
            },
          }
        : { enabled: false },
    },
  });
};

// Only start the SDK when APM is enabled and required env vars are present.
initApm();
